import { ClockCircleOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { DatePicker, Divider, Input, Select, Switch, Typography } from 'antd';
import dayjs from 'dayjs';

import { BankType } from '~apis/wiring';

import { CompressInfo, ReservationInfo } from './types';

interface WiringTypeSelectorProps {
  selectedBank: BankType;
  reservationInfo?: ReservationInfo;
  compressInfo?: CompressInfo;
  isPayrollMode: boolean;
  setReservationInfo: (info: ReservationInfo) => void;
  setIsPayrollMode: (checked: boolean) => void;
  setCompressInfo: (info: CompressInfo) => void;
}

const WiringTypeSelector: React.FC<WiringTypeSelectorProps> = ({
  selectedBank,
  reservationInfo,
  isPayrollMode,
  compressInfo,
  setCompressInfo,
  setIsPayrollMode,
  setReservationInfo,
}) => {
  const options = dayjs(reservationInfo?.reservationDate).isSame(dayjs(), 'day')
    ? Array.from({ length: 24 }, (_, i) => i)
        .map((hour) => ({
          label: `${hour}시`,
          value: hour,
        }))
        .filter((item) => {
          return !(item.value < dayjs().hour() + 1 + (dayjs().minute() !== 0 ? 1 : 0));
        })
    : [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map((hour) => ({
        label: `${hour}시`,
        value: hour,
      }));

  return (
    <Flex
      align="center"
      gap={24}
      style={{
        background: 'rgba(0, 0, 0, 0.02)',
        padding: 16,
        borderRadius: 8,
      }}
    >
      <Flex gap={8} align="center">
        <Flex align="center" gap={4}>
          <Typography>급여 이체</Typography>
        </Flex>
        <Switch
          size="small"
          disabled={!selectedBank}
          checked={isPayrollMode}
          onChange={(checked) => {
            setIsPayrollMode(checked);
          }}
        />
      </Flex>
      <Divider
        type="vertical"
        style={{
          width: '1px',
          background: colorTokens.borderDefault,
        }}
      />
      <Flex gap={8} align="center">
        <Flex align="center" gap={4}>
          <Typography>예약 이체</Typography>
        </Flex>
        <Switch
          disabled={!selectedBank || selectedBank === 'HANA'}
          size="small"
          checked={reservationInfo?.isReservation}
          onChange={(checked) => {
            setReservationInfo({
              ...reservationInfo,
              isReservation: checked,
            });
          }}
        />
        {!selectedBank || selectedBank === 'HANA' ? (
          <Typography
            style={{
              color: colorTokens.textDescription,
            }}
          >
            하나은행은 3단계 은행 연결에서 예약 여부를 선택할 수 있어요.
          </Typography>
        ) : reservationInfo?.isReservation ? (
          <Flex gap={8} align="center">
            <DatePicker
              style={{ width: 160 }}
              placeholder="예약일자"
              disabled={!reservationInfo?.isReservation}
              value={
                reservationInfo?.reservationDate
                  ? dayjs(reservationInfo.reservationDate)
                  : undefined
              }
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              onChange={(value) => {
                setReservationInfo({
                  ...reservationInfo,
                  reservationDate: value?.format('YYYY-MM-DD'),
                });
              }}
            />
            <Select
              placeholder="예약시간"
              suffixIcon={<ClockCircleOutlined />}
              options={options}
              value={
                reservationInfo?.reservationTime
                  ? dayjs(reservationInfo.reservationTime, 'HH:mm:ss').hour()
                  : undefined
              }
              onChange={(value) => {
                setReservationInfo({
                  ...reservationInfo,
                  reservationTime: dayjs(`${dayjs().format('YYYY-MM-DD')} ${value}:00`).format(
                    'HH:mm:ss',
                  ),
                });
              }}
              style={{
                width: 80,
              }}
            />
          </Flex>
        ) : (
          <div />
        )}
      </Flex>
      <Divider
        type="vertical"
        style={{
          width: '1px',
          background: colorTokens.borderDefault,
        }}
      />
      <Flex gap={8} align="center">
        <Flex align="center" gap={4}>
          <Typography
            style={{
              width: 'max-content',
            }}
          >
            압축 기장
          </Typography>
        </Flex>
        <Switch
          disabled={!selectedBank || selectedBank === 'HANA'}
          size="small"
          checked={compressInfo?.isCompress}
          onChange={(checked) => {
            setCompressInfo({
              ...compressInfo,
              isCompress: checked,
            });
          }}
        />
        {!selectedBank || selectedBank === 'HANA' ? (
          <Typography
            style={{
              color: colorTokens.textDescription,
            }}
          >
            하나은행은 3단계 은행 연결에서 압축 기장 여부를 선택할 수 있어요.
          </Typography>
        ) : compressInfo?.isCompress ? (
          <Input
            prefix="_"
            disabled={!compressInfo?.isCompress}
            placeholder="압축 기장 적요를 입력하세요 (최대 6자)"
            style={{
              width: 360,
            }}
            maxLength={6}
            value={compressInfo?.compressMemo}
            onChange={(e) => {
              setCompressInfo({
                ...compressInfo,
                compressMemo: e.target.value,
              });
            }}
          />
        ) : (
          <div />
        )}
      </Flex>
    </Flex>
  );
};

export default WiringTypeSelector;
